/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { ActionContext, Module } from 'vuex';
import { extend, flow, cloneDeep, get, find, findIndex, isEmpty } from 'lodash';
import { format, parseISO } from 'date-fns';
import {
  STEP_DEFAULTS,
  isStepDirty,
  computeValidationErrors,
  validateEachStepsTriggersDuplication,
  triggerChanged,
  updateModified,
} from './workflowsHelpers';
import { keysToCamel } from '@/utils';
import { RootState } from '@/state/types';

interface State {
  workflowsGqlResponse: Array<Object>;
  originalWorkflows: Array<Object>;
  modifiedWorkflows: Array<Object>;
  stepsGqlResponse: Array<Object>;
  modifiedSteps: Array<Object>;
}

const state: State = {
  workflowsGqlResponse: [],
  originalWorkflows: [],
  modifiedWorkflows: [],
  stepsGqlResponse: [],
  modifiedSteps: [],
};

export const getters = {
  originalWorkflows(_: State, getters): Array<Object> {
    if (!state.workflowsGqlResponse) {
      return [];
    }

    const parseGqlResponse = gqlWorkflows =>
      gqlWorkflows.map((edge: any) => ({
        id: get(edge, 'node.id'),
        title: get(edge, 'node.title.value'),
        date: format(parseISO(get(edge, 'node.updatedAt.value')), 'd/MMM/yyyy h:mm a'),
        invalid: state.modifiedSteps.some(
          step => (!isEmpty(step.validationErrors) || step.serverError) && step.groupId === get(edge, 'node.id')
        ),
        dirty: getters.steps.some(step => step.dirty && step.groupId === get(edge, 'node.id')),
      }));

    return flow(parseGqlResponse)(state.workflowsGqlResponse);
  },
  modifiedWorkflows(state: State) {
    return state.modifiedWorkflows;
  },
  workflows(_: State, getters): Array<Object> {
    const applyUserModifications = workflows => {
      workflows.forEach(workflow => extend(workflow, find(getters.modifiedWorkflows, { id: workflow.id })));
      let nonexisting = getters.modifiedWorkflows.filter(modified => !find(workflows, { id: modified.id }));
      workflows = workflows.concat(nonexisting);
      return workflows;
    };
    return flow(cloneDeep, applyUserModifications)(getters.originalWorkflows);
  },
  originalSteps(state: State): Array<Object> {
    if (!state.stepsGqlResponse) {
      return [];
    }

    const parseGqlResponse = gqlSteps =>
      gqlSteps.map((edge: any) => {
        const threshold = get(edge, 'node.riskThreshold.value');
        const riskThreshold = threshold
          ? { riskThreshold: { id: threshold.id, label: get(threshold, 'title.value') } }
          : {};
        return {
          id: get(edge, 'node.id'),
          active: get(edge, 'node.active.value'),
          stepTitle: get(edge, 'node.title.value'),
          actionTrigger: get(edge, 'node.actionTrigger.value'),
          ...riskThreshold,
          sourceType: get(edge, 'node.sourceType.value'),
          triggerType: get(edge, 'node.triggerType.value'),
          groupId: get(edge, 'node.workflowGroup.value.id'),
          order: get(edge, 'node.order.value'),
          ...keysToCamel(JSON.parse(get(edge, 'node.content.value') || '{}')),
        };
      });

    const ensureStepProps = steps => {
      return steps.map(step => extend({}, STEP_DEFAULTS, step));
    };

    return flow(parseGqlResponse, ensureStepProps)(state.stepsGqlResponse);
  },
  steps(_: State, getters): Array<Object> {
    const checkDirtiness = steps => {
      steps.forEach(step => {
        let modStep = find(getters.modifiedSteps, { id: step.id });
        step.dirty = modStep && isStepDirty(modStep, getters.originalSteps);
      });
      return steps;
    };

    const applyUserModifications = steps => {
      steps.forEach(step => extend(step, find(getters.modifiedSteps, { id: step.id })));
      let nonexisting = getters.modifiedSteps.filter(modified => !find(steps, { id: modified.id }));
      steps = steps.concat(nonexisting);
      return steps;
    };

    const sortByOrder = steps => steps.sort((a, b) => a.order - b.order);

    return flow(cloneDeep, applyUserModifications, checkDirtiness, sortByOrder)(getters.originalSteps);
  },
  modifiedSteps(state: State) {
    return state.modifiedSteps;
  },
};

export const mutations = {
  SET_WORKFLOWS_GQL_RESPONSE(state: State, gqlWorkflows: Array<Object>) {
    state.workflowsGqlResponse = gqlWorkflows;
  },
  SET_STEPS_GQL_RESPONSE(state: State, steps: Array<Object>) {
    state.stepsGqlResponse = steps;
  },
  UPDATE_STEP(state: State, payload: { id: string; data: Object; originalSteps: Array<Object>; steps: Array<Object> }) {
    let step = find(payload.steps, { id: payload.id });
    let modifiedStep = updateModified(payload.id, payload.data, state.modifiedSteps, payload.originalSteps);
    modifiedStep.validationErrors = computeValidationErrors(modifiedStep, payload.steps);

    // Hack: Manually updates the step list with the payload data
    extend(find(payload.steps, { id: payload.id }), modifiedStep);
    if (triggerChanged(step, payload.data)) {
      validateEachStepsTriggersDuplication(state.modifiedSteps, payload.steps);
    }
  },
  UPDATE_WORKFLOW(state: State, payload: { id: string; workflow: Object; originalWorkflows: Array<Object> }) {
    let item = find(state.modifiedWorkflows, { id: payload.id });
    if (item) {
      extend(item, payload.workflow);
    } else {
      let item = find(payload.originalWorkflows, { id: payload.id });
      if (item) {
        payload.workflow = extend({}, item, payload.workflow);
      }
      state.modifiedWorkflows.push(cloneDeep(payload.workflow));
    }
  },
  DELETE_MODIFIED_STEP(state: State, stepId: string) {
    const index = findIndex(state.modifiedSteps, { id: stepId });
    if (index >= 0) {
      state.modifiedSteps.splice(index, 1);
      validateEachStepsTriggersDuplication(state.modifiedSteps, state.modifiedSteps);
    }
  },
  DELETE_MODIFIED_WORKFLOW(state: State, workflowId: string) {
    const index = findIndex(state.modifiedWorkflows, { id: workflowId });
    if (index >= 0) {
      state.modifiedWorkflows.splice(index, 1);
    }
    const newModifiedSteps = state.modifiedSteps.filter(o => o.groupId !== workflowId);
    if (newModifiedSteps.length !== state.modifiedSteps.length) {
      state.modifiedSteps = newModifiedSteps;
      validateEachStepsTriggersDuplication(state.modifiedSteps, state.modifiedSteps);
    }
  },
};

export const actions = {
  setWorkflowsGqlResponse({ commit }: ActionContext, gqlWorkflows: Array<Object>) {
    commit('SET_WORKFLOWS_GQL_RESPONSE', cloneDeep(gqlWorkflows));
  },
  setStepsGqlResponse({ commit }: ActionContext, stepsGqlResponse: Array<Object>) {
    commit('SET_STEPS_GQL_RESPONSE', cloneDeep(stepsGqlResponse));
  },
  updateStep({ commit, getters }: ActionContext, payload: { id: number; data: Object }) {
    if (!find(getters.steps, { id: payload.id })) return;
    commit('UPDATE_STEP', {
      id: payload.id,
      data: payload.data,
      originalSteps: getters.originalSteps,
      steps: getters.steps,
    });
  },
  createStep({ commit, getters }: ActionContext, step: Object) {
    commit('UPDATE_STEP', {
      data: step,
      originalSteps: getters.originalSteps,
      steps: getters.steps,
    });
  },
  createWorkflow({ commit, getters }: ActionContext, workflow: Object) {
    commit('UPDATE_WORKFLOW', {
      workflow,
      originalWorkflows: getters.originalWorkflows,
    });
  },
  updateWorkflow({ commit, getters }: ActionContext, payload: { id: number; workflow: Object }) {
    commit('UPDATE_WORKFLOW', {
      id: payload.id,
      workflow: payload.workflow,
      originalWorkflows: getters.originalWorkflows,
    });
  },
  deleteStepFromModified({ commit }: ActionContext, stepId: String) {
    commit('DELETE_MODIFIED_STEP', stepId);
  },
  deleteWorkflowFromModified({ commit }: ActionContext, workflowId: String) {
    commit('DELETE_MODIFIED_WORKFLOW', workflowId);
  },
};

export default (): Module<State, RootState> => ({
  namespaced: true,
  strict: false,
  state,
  getters,
  mutations,
  actions,
});
